// Guided wizard step popups for the Designer, extracted verbatim from
// Designer.tsx. Triggered when a user clicks a step pill in the EditorWizardBar;
// each step renders a slim version of the relevant inputs. Presentational: all
// state + actions (product/colour, location switching, placement, sizes) are
// owned by the page and forwarded via props. The "Add artwork" step (3) is not
// here — it opens the dedicated AddDesignModal via the page's gotoWizStep.
import type { CatProduct, PrintLocation } from '../../lib/catalog-types'
import type { GarmentTotals } from './helpers'
import WizardStepModal from '../../components/WizardStepModal'

export interface DesignerWizardModalsProps {
  wizModalStep: string | null
  setWizModalStep: (v: string | null) => void
  gotoWizStep: (dir: 'next' | 'prev') => void
  product: CatProduct
  garments: CatProduct[]
  productSlug: string
  setProductSlug: (slug: string) => void
  colourId: string | null
  setColourId: (id: string) => void
  activeLocationId: string | null
  activeLocation: PrintLocation | null
  switchLocation: (nextId: string) => void
  hasArtwork: boolean
  placementCenter: () => void
  placementFit: (mode: 'width' | 'height' | 'contain') => void
  placementScale: (factor: number) => void
  qty: Record<string, number>
  setQty: (q: Record<string, number>) => void
  setNeedsSizes: (v: boolean) => void
  totals: GarmentTotals
}

export default function DesignerWizardModals({
  wizModalStep, setWizModalStep, gotoWizStep, product, garments, productSlug, setProductSlug,
  colourId, setColourId, activeLocationId, activeLocation, switchLocation, hasArtwork,
  placementCenter, placementFit, placementScale, qty, setQty, setNeedsSizes, totals,
}: DesignerWizardModalsProps) {
  // Derive the active colour from product + colourId (matches Designer's colour
  // useMemo, fallback included) so the wizard doesn't need a redundant prop.
  const colour = product.colours.find((c) => c.id === colourId) ?? product.colours[0]
  return (
    <>
      <WizardStepModal
        open={wizModalStep === 'product'}
        stepNumber={1} totalSteps={6}
        title="Pick your garment & colour"
        subtitle="What are we printing on?"
        onClose={() => setWizModalStep(null)}
        onNext={() => gotoWizStep('next')}
        nextDisabled={!product || !colourId}>
        <div className="space-y-5">
          <div>
            <div className="eyebrow">Garment</div>
            <div className="space-y-1.5">
              {garments.map((g) => (
                <button key={g.id} onClick={() => setProductSlug(g.slug)}
                  className={`w-full text-left px-3 py-2.5 rounded border transition-colors ${productSlug === g.slug ? 'border-brick bg-brick-tint' : 'border-border hover:border-ink-3'}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-ink text-sm">{g.name}</span>
                    <span className="text-xs text-ink-3">from ${Math.min(...g.variants.map((v) => v.price)).toFixed(0)}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
          {product && (
            <div>
              <div className="eyebrow">Colour {colour && <span className="text-ink-3 font-normal normal-case tracking-normal">— {colour.name}</span>}</div>
              <div className="flex flex-wrap gap-2.5">
                {product.colours.map((c) => (
                  <button key={c.id} title={c.name} onClick={() => setColourId(c.id)}
                    className={`w-8 h-8 rounded-full ring-2 ring-offset-2 ring-offset-cream transition-all ${colourId === c.id ? 'ring-brick' : 'ring-transparent hover:ring-brick/40'}`}
                    style={{ background: c.hex }} />
                ))}
              </div>
            </div>
          )}
        </div>
      </WizardStepModal>

      <WizardStepModal
        open={wizModalStep === 'location'}
        stepNumber={2} totalSteps={6}
        title="Which side of the garment?"
        subtitle="Pick where the artwork should land. You can add to more sides after."
        onClose={() => setWizModalStep(null)}
        onPrev={() => gotoWizStep('prev')}
        onNext={() => gotoWizStep('next')}
        nextDisabled={!activeLocationId}>
        <div className="grid grid-cols-2 gap-2.5">
          {(product?.printLocations ?? []).map((loc) => (
            <button key={loc.id} onClick={() => switchLocation(loc.id)}
              className={`px-3 py-3 rounded border text-left transition-colors ${activeLocationId === loc.id ? 'border-brick bg-brick-tint' : 'border-border hover:border-ink-3 bg-white'}`}>
              <div className="text-xs uppercase tracking-wider text-ink-3 font-bold">Location</div>
              <div className="font-semibold text-ink text-sm mt-0.5">{loc.name}</div>
            </button>
          ))}
          {(product?.printLocations?.length ?? 0) === 0 && (
            <p className="col-span-2 text-sm text-ink-3 italic">This garment has no print locations yet — pick another one first.</p>
          )}
        </div>
      </WizardStepModal>

      {/* The "Add artwork" step uses the dedicated AddDesignModal
          (rendered below) with Upload + AI tabs, not the slim
          WizardStepModal. Step 3 is wired to setAddDesignOpen(true)
          in the wizardSteps array + gotoWizStep helper above. */}

      <WizardStepModal
        open={wizModalStep === 'placement'}
        stepNumber={4} totalSteps={6}
        title="Artwork placement"
        subtitle={`Position your design inside the print area on the ${activeLocation?.name ?? 'garment'}. Drag and resize on the canvas, or use the quick actions below.`}
        onClose={() => setWizModalStep(null)}
        onPrev={() => gotoWizStep('prev')}
        onNext={() => gotoWizStep('next')}
        nextDisabled={!hasArtwork}>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button onClick={placementCenter} disabled={!hasArtwork}
              className="px-3 py-2.5 rounded border border-border bg-white text-sm font-semibold text-ink hover:border-brick hover:text-brick disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9"/><line x1="12" y1="3" x2="12" y2="21"/><line x1="3" y1="12" x2="21" y2="12"/>
              </svg>
              Center on print area
            </button>
            <button onClick={() => placementFit('contain')} disabled={!hasArtwork}
              className="px-3 py-2.5 rounded border border-border bg-white text-sm font-semibold text-ink hover:border-brick hover:text-brick disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="5" width="18" height="14" rx="1"/><polyline points="8 12 12 8 16 12"/><polyline points="8 16 12 20 16 16"/>
              </svg>
              Fit inside
            </button>
            <button onClick={() => placementFit('width')} disabled={!hasArtwork}
              className="px-3 py-2.5 rounded border border-border bg-white text-sm font-semibold text-ink hover:border-brick hover:text-brick disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="12" x2="21" y2="12"/><polyline points="6 9 3 12 6 15"/><polyline points="18 9 21 12 18 15"/>
              </svg>
              Fit width
            </button>
            <button onClick={() => placementFit('height')} disabled={!hasArtwork}
              className="px-3 py-2.5 rounded border border-border bg-white text-sm font-semibold text-ink hover:border-brick hover:text-brick disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="3" x2="12" y2="21"/><polyline points="9 6 12 3 15 6"/><polyline points="9 18 12 21 15 18"/>
              </svg>
              Fit height
            </button>
            <button onClick={() => placementScale(0.9)} disabled={!hasArtwork}
              className="px-3 py-2.5 rounded border border-border bg-white text-sm font-semibold text-ink hover:border-brick hover:text-brick disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              − Smaller
            </button>
            <button onClick={() => placementScale(1.1)} disabled={!hasArtwork}
              className="px-3 py-2.5 rounded border border-border bg-white text-sm font-semibold text-ink hover:border-brick hover:text-brick disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
              + Bigger
            </button>
          </div>
          {!hasArtwork && (
            <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
              You need to add artwork first — go back to step 3.
            </div>
          )}
          <p className="text-[11px] text-ink-3 leading-relaxed">
            <strong className="text-ink-2">Tip:</strong> You can also drag the design directly on the garment canvas, and the corner handles let you resize it freehand. The print-area border shows the safe zone for printing.
          </p>
        </div>
      </WizardStepModal>

      <WizardStepModal
        open={wizModalStep === 'sizes'}
        stepNumber={5} totalSteps={6}
        title="Sizes & quantity"
        subtitle="How many of each size?"
        onClose={() => setWizModalStep(null)}
        onPrev={() => gotoWizStep('prev')}
        onNext={() => gotoWizStep('next')}
        nextLabel={`Submit ($${totals.total.toFixed(2)}) →`}
        nextDisabled={totals.totalQty === 0}>
        <div className="space-y-2">
          {(product?.variants ?? []).filter((v) => v.size).map((v) => (
            <div key={v.id} className="flex items-center gap-3 text-sm rounded px-2 py-1.5 bg-white border border-border">
              <span className="w-12 text-xs font-semibold text-ink uppercase">{v.size}</span>
              <input type="number" min={0} value={qty[v.size!] ?? 0}
                onChange={(e) => {
                  const n = Math.max(0, parseInt(e.target.value) || 0)
                  setQty({ ...qty, [v.size!]: n })
                  if (n > 0) setNeedsSizes(false)
                }}
                className="w-20 px-2 py-1 bg-white border border-border rounded text-ink text-sm focus:outline-none focus:border-brick" />
              <span className="text-xs text-ink-3 ml-auto">${v.price.toFixed(2)} ea</span>
            </div>
          ))}
          <div className="border-t border-brick/30 mt-3 pt-3 text-sm space-y-1">
            <div className="flex justify-between"><span className="text-ink-3">Total qty</span><span className="text-ink font-semibold">{totals.totalQty}</span></div>
            <div className="flex justify-between text-base"><span className="text-ink-2 font-semibold">Total</span><span className="text-brick font-bold">${totals.total.toFixed(2)}</span></div>
          </div>
        </div>
      </WizardStepModal>
    </>
  )
}
